import { createApi } from '@reduxjs/toolkit/query/react'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import { PromotionNomination, PromotionNominationDetails } from '@/features/promotion/promotion.def.ts'
import {
  CancelPromotionNominationRequest,
  CreatePromotionNominationRequest,
  FinalizePromotionNominationRequest,
  GetPromotionNominationByIdRequest,
  ReplyPromotionApprovalRequest,
  SubmitForApprovalRequest,
  SubmitForReviewRequest,
  UpdatePromotionNominationRequest,
} from '@/features/promotion/promotion-request.def.ts'

export const promotionApi = createApi({
  reducerPath: 'promotionApi',
  baseQuery: hcPortalBaseQuery(),
  tagTypes: ['PromotionNomination'],
  endpoints: (builder) => ({
    getPromotionNominations: builder.query<PromotionNominationDetails[], void>({
      query: () => ({
        url: 'Promotion/GetPromotionNominations',
        method: 'GET',
      }),
      providesTags: () => [{ type: 'PromotionNomination', id: 'LIST' }],
    }),
    getPromotionNominationById: builder.query<PromotionNominationDetails, GetPromotionNominationByIdRequest>({
      query: (request) => ({
        url: 'Promotion/GetPromotionNominationById',
        params: request,
      }),
      providesTags: (_, __, { id }) => [{ type: 'PromotionNomination', id }],
    }),
    createPromotionNomination: builder.mutation<PromotionNomination, CreatePromotionNominationRequest>({
      query: (request) => ({
        url: 'Promotion/CreatePromotionNomination',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [{ type: 'PromotionNomination', id: 'LIST' }],
    }),
    updatePromotionNomination: builder.mutation<boolean, UpdatePromotionNominationRequest>({
      query: (request) => ({
        url: 'Promotion/UpdatePromotionNomination',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'PromotionNomination', id }],
    }),
    submitForReview: builder.mutation<boolean, SubmitForReviewRequest>({
      query: (request) => ({
        url: 'Promotion/SubmitForReview',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'PromotionNomination', id }],
    }),
    submitForApproval: builder.mutation<boolean, SubmitForApprovalRequest>({
      query: (request) => ({
        url: 'Promotion/SubmitForApproval',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'PromotionNomination', id }],
    }),
    replyPromotionApproval: builder.mutation<boolean, ReplyPromotionApprovalRequest>({
      query: (request) => ({
        url: 'Promotion/ReplyPromotionApproval',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'PromotionNomination', id }],
    }),
    advancePromotionApprovalStage: builder.mutation<boolean, SubmitForApprovalRequest>({
      query: (request) => ({
        url: 'Promotion/AdvancePromotionApprovalStage',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'PromotionNomination', id }],
    }),
    finalizePromotionNomination: builder.mutation<boolean, FinalizePromotionNominationRequest>({
      query: (request) => ({
        url: 'Promotion/FinalizePromotionNomination',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'PromotionNomination', id }],
    }),
    cancelPromotionNomination: builder.mutation<boolean, CancelPromotionNominationRequest>({
      query: (request) => ({
        url: 'Promotion/CancelPromotionNomination',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'PromotionNomination', id }],
    })
  }),
})

export const {
  useGetPromotionNominationsQuery,
  useGetPromotionNominationByIdQuery,
  useCreatePromotionNominationMutation,
  useUpdatePromotionNominationMutation,
  useSubmitForReviewMutation,
  useSubmitForApprovalMutation,
  useReplyPromotionApprovalMutation,
  useAdvancePromotionApprovalStageMutation,
  useFinalizePromotionNominationMutation,
  useCancelPromotionNominationMutation,
} = promotionApi
